import React, { useEffect, useState } from "react";
import { Box, Button, List, ListItem, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import TaskHeader from "components/task/header/TaskHeader";
import CustomModal from "components/common/CustomModal";
import Picker, { PickerValue } from "react-mobile-picker";
import {
  NOTIFICATION_SETTINGS_DETAIL_ENDPOINT,
  NOTIFICATION_SETTINGS_DOMAIN,
  notificationSettingsApi,
  NotificationSettingsType,
  NotificationSettingsUpdateType,
  NotificationSettingsDeleteType,
} from "../../api/notificationSettingsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckIconSvgSelectedButton } from "../../static/image/common/SvgAssets";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import { sendMessage } from "../../utils/webview";
import ContentTemplateV2 from "../../components/task/content/ContentTemplateV2";

// 요일 데이터 배열 (기존과 동일)
const daysOfWeek = [
  { key: 1, label: "월요일마다" },
  { key: 2, label: "화요일마다" },
  { key: 3, label: "수요일마다" },
  { key: 4, label: "목요일마다" },
  { key: 5, label: "금요일마다" },
  { key: 6, label: "토요일마다" },
  { key: 7, label: "일요일마다" },
];

// 시간 변환 함수들 (기존과 동일)
const parseTimeString = (timeString: string) => {
  const [hours, minutes] = timeString.split(":");
  const hour = parseInt(hours, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;

  return {
    hour: formattedHour,
    minute: minutes,
    period: period,
  };
};

const formatTimeString = (timeObj: PickerValue) => {
  let hour = timeObj.hour as number;
  if (timeObj.period === "PM" && hour !== 12) {
    hour += 12;
  } else if (timeObj.period === "AM" && hour === 12) {
    hour = 0;
  }

  return `${hour.toString().padStart(2, "0")}:${timeObj.minute}`;
};

interface NotificationSettingsUpdatePageProps {
  type: "thoughtRecord" | "meditationRecord";
  index?: number;
  notification?: NotificationSettingsType;
}

export default function NotificationSettingsUpdatePage() {
  const user = useRecoilValue(userAtom);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isInappWebview = useIsInappWebview();

  const props = (location.state as NotificationSettingsUpdatePageProps) || {
    type: "thoughtRecord",
  };

  // --- 기존 로직은 모두 동일하게 유지 ---
  const getNearestFutureTime = () => {
    const now = new Date();
    const currentMinutes = now.getMinutes();
    const minutesRemainder = currentMinutes % 15;
    const addMinutes = minutesRemainder === 0 ? 15 : 15 - minutesRemainder;

    const totalMinutes = now.getHours() * 60 + currentMinutes + addMinutes;
    const newHour24 = Math.floor(totalMinutes / 60) % 24;
    const newMinute = totalMinutes % 60;

    const period = newHour24 >= 12 ? "PM" : "AM";
    const hour12 = newHour24 % 12 || 12;

    return {
      hour: hour12,
      minute: newMinute.toString().padStart(2, "0"),
      period: period,
    } as { hour: number; minute: string; period: "AM" | "PM" };
  };

  const defaultTime = getNearestFutureTime();
  const defaultDays = [1, 2, 3, 4, 5, 6, 7];
  const initialTime = props.notification
    ? parseTimeString(props.notification.time)
    : defaultTime;

  const [selectedDays, setSelectedDays] = useState<number[]>(
    props.notification ? props.notification.dayOfWeekList : defaultDays
  );

  const BASE_HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
  const BASE_MINUTES = ["00", "15", "30", "45"];
  const REPEAT = 50;
  const TOTAL_HOUR_ITEMS = BASE_HOURS.length * REPEAT;
  const TOTAL_MINUTE_ITEMS = BASE_MINUTES.length * REPEAT;

  const getHourFromIndex = (idx: number) =>
    BASE_HOURS[
      ((idx % BASE_HOURS.length) + BASE_HOURS.length) % BASE_HOURS.length
    ];
  const getMinuteFromIndex = (idx: number) =>
    BASE_MINUTES[
      ((idx % BASE_MINUTES.length) + BASE_MINUTES.length) % BASE_MINUTES.length
    ];

  const getCenterAlignedStart = (total: number, baseLen: number) =>
    Math.floor(total / 2) - (Math.floor(total / 2) % baseLen);

  const centerHourStart = getCenterAlignedStart(
    TOTAL_HOUR_ITEMS,
    BASE_HOURS.length
  );
  const centerMinuteStart = getCenterAlignedStart(
    TOTAL_MINUTE_ITEMS,
    BASE_MINUTES.length
  );

  const baseHourIdx = BASE_HOURS.indexOf(initialTime.hour);
  const baseMinuteIdx = BASE_MINUTES.indexOf(initialTime.minute);
  const initialHourIndex =
    centerHourStart + (baseHourIdx === -1 ? 0 : baseHourIdx);
  const initialMinuteIndex =
    centerMinuteStart + (baseMinuteIdx === -1 ? 0 : baseMinuteIdx);

  const [timeValueGroups, setTimeValueGroups] = useState<PickerValue>({
    hour: initialHourIndex,
    minute: initialMinuteIndex,
    period: initialTime.period,
  });

  const timeOptionGroups = {
    hour: Array.from({ length: TOTAL_HOUR_ITEMS }, (_, i) => i),
    minute: Array.from({ length: TOTAL_MINUTE_ITEMS }, (_, i) => i),
    period: ["AM", "PM"],
  };

  const recenterIndex = (index: number, total: number, baseLen: number) => {
    const basePos = ((index % baseLen) + baseLen) % baseLen;
    const centerStart =
      Math.floor(total / 2) - (Math.floor(total / 2) % baseLen);
    return centerStart + basePos;
  };

  const handleTimeChange = (newValue: PickerValue) => {
    let nextHourIndex = newValue.hour as number;
    let nextMinuteIndex = newValue.minute as number;

    const hourEdge = BASE_HOURS.length;
    const minuteEdge = BASE_MINUTES.length;

    if (
      nextHourIndex < hourEdge ||
      nextHourIndex > TOTAL_HOUR_ITEMS - hourEdge - 1
    ) {
      nextHourIndex = recenterIndex(
        nextHourIndex,
        TOTAL_HOUR_ITEMS,
        BASE_HOURS.length
      );
    }
    if (
      nextMinuteIndex < minuteEdge ||
      nextMinuteIndex > TOTAL_MINUTE_ITEMS - minuteEdge - 1
    ) {
      nextMinuteIndex = recenterIndex(
        nextMinuteIndex,
        TOTAL_MINUTE_ITEMS,
        BASE_MINUTES.length
      );
    }

    setTimeValueGroups({
      hour: nextHourIndex,
      minute: nextMinuteIndex,
      period: newValue.period,
    });
  };

  const toggleDay = (dayKey: number) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayKey)) {
        return prev.filter((day) => day !== dayKey);
      } else {
        return [...prev, dayKey].sort();
      }
    });
  };

  const { mutate: updateNotification, isLoading } = useMutation(
    (data: NotificationSettingsUpdateType) =>
      notificationSettingsApi.update(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          NOTIFICATION_SETTINGS_DOMAIN,
          NOTIFICATION_SETTINGS_DETAIL_ENDPOINT,
          user?.userId,
        ]);
        navigate(-1);
      },
      onError: (error) => {
        console.error("알림 설정 업데이트 실패:", error);
      },
    }
  );

  const { mutate: deleteNotification, isLoading: isDeleting } = useMutation(
    (data: NotificationSettingsDeleteType) =>
      notificationSettingsApi.delete(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          NOTIFICATION_SETTINGS_DOMAIN,
          NOTIFICATION_SETTINGS_DETAIL_ENDPOINT,
          user?.userId,
        ]);
        navigate(-1);
      },
      onError: (error) => {
        console.error("알림 설정 삭제 실패:", error);
      },
    }
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const handleConfirmDelete = () => {
    if (props.index === undefined) return;
    deleteNotification({
      type: props.type,
      index: props.index,
    });
  };

  const handleSave = () => {
    const logicalHour = getHourFromIndex(timeValueGroups.hour as number);
    const logicalMinute = getMinuteFromIndex(timeValueGroups.minute as number);
    const normalized: PickerValue = {
      hour: logicalHour,
      minute: logicalMinute,
      period: timeValueGroups.period,
    };

    const timeString = formatTimeString(normalized);

    const updateData: NotificationSettingsUpdateType = {
      type: props.type,
      index: props.index,
      notification: {
        time: timeString,
        dayOfWeekList: selectedDays,
      },
    };

    updateNotification(updateData);
  };

  const selectedDaySet = new Set(selectedDays);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const isEdit = props.index !== undefined;
  const currentTimeString = (() => {
    const logicalHour = getHourFromIndex(timeValueGroups.hour as number);
    const logicalMinute = getMinuteFromIndex(timeValueGroups.minute as number);
    return formatTimeString({
      hour: logicalHour,
      minute: logicalMinute,
      period: timeValueGroups.period as "AM" | "PM",
    });
  })();
  const arraysEqual = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    const as = [...a].sort();
    const bs = [...b].sort();
    return as.every((v, i) => v === bs[i]);
  };
  const hasChangesEdit =
    isEdit &&
    !!props.notification &&
    (currentTimeString !== props.notification.time ||
      !arraysEqual(selectedDays, props.notification.dayOfWeekList));
  const isSaveActive = isEdit
    ? hasChangesEdit
    : !isLoading && selectedDays.length > 0;
  const handleBack = () => {
    if (isSaveActive) {
      setLeaveModalOpen(true);
    } else {
      navigate(-1);
    }
  };

  // useEffect(() => {
  //   if (isInappWebview) {
  //     const msg = JSON.stringify({
  //       event: "set_dirty",
  //       params: {
  //         isDirty: isSaveActive,
  //       },
  //     });
  //     sendMessage(msg);
  //   }
  // }, [isSaveActive, isInappWebview]);

  return (
    <>
      {/* 상단 헤더 고정 */}
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <TaskHeader
              taskKey={`new-notification-page`}
              title={props.index !== undefined ? "알림 변경" : "새 알림"}
              onBack={handleBack}
            />
            <Button
              variant="Solid"
              size="sm"
              onClick={handleSave}
              disabled={isLoading || (isEdit && !hasChangesEdit)}
              sx={{ position: "absolute", right: "20px" }}
            >
              저장
            </Button>
          </Box>
        </Box>

        {/* 본문 영역: 위(Picker)는 고정, 아래만 스크롤 */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Picker: 고정 영역 */}
          <Box
            sx={{
              flexShrink: 0,
              width: "198px",
              height: "220px",
              margin: "0 auto",
              position: "relative",
              WebkitMaskImage:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 40%, #000 60%, rgba(0, 0, 0, 0.00) 100%)",
              maskImage:
                "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 40%, #000 60%, rgba(0, 0, 0, 0.00) 100%)",
              "& > div > div > div": {
                background: "transparent !important",
              },
              "& > div > div": {
                padding: "0",
              },
              "& > div > div > div:nth-of-type(1)": {
                maxWidth: "20px",
                margin: "0 auto",
              },
              "& > div > div > div:nth-of-type(2)": {
                maxWidth: "80px",
                margin: "0 auto",
              },
              "& > div > div > div:nth-of-type(3)": {
                maxWidth: "80px",
                margin: "0 auto",
              },
            }}
          >
            <Picker
              value={timeValueGroups}
              onChange={handleTimeChange}
              itemHeight={44}
              height={220}
              wheelMode="natural"
              style={{ gap: "44px" }}
            >
              {Object.keys(timeOptionGroups).map((name) => (
                <Picker.Column key={name} name={name}>
                  {timeOptionGroups[name as keyof typeof timeOptionGroups].map(
                    (option) => (
                      <Picker.Item key={option} value={option}>
                        {({ selected }) => (
                          <div
                            style={{
                              fontSize: "28px",
                              fontWeight: 590,
                              color: selected
                                ? theme.vars.palette.Content.Strong
                                : theme.vars.palette.Content.SuperWeak,
                            }}
                          >
                            {name === "hour"
                              ? getHourFromIndex(option as number)
                              : name === "minute"
                                ? getMinuteFromIndex(option as number)
                                : option}
                          </div>
                        )}
                      </Picker.Item>
                    )
                  )}
                </Picker.Column>
              ))}
            </Picker>
          </Box>

          {/* 아래 영역만 스크롤되도록 ContentTemplateV2를 분리 */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ContentTemplateV2>
              {/* 기존과 동일하게 여유 간격이 필요하면 mt로 간격 유지 */}
              <Box
                sx={{
                  backgroundColor: theme.vars.palette.Surface.Forward,
                  borderRadius: "16px",
                  mt: "12px",
                }}
              >
                <List sx={{ py: 1 }}>
                  {daysOfWeek.map((day) => (
                    <ListItem
                      key={day.key}
                      onClick={() => toggleDay(day.key)}
                      sx={{ px: "20px", py: "13.5px", cursor: "pointer" }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                      >
                        <CustomTypography
                          variant="Body"
                          sx={{
                            fontWeight: selectedDaySet.has(day.key) ? 700 : 500,
                            color: selectedDaySet.has(day.key)
                              ? theme.vars.palette.Content.Strong
                              : theme.vars.palette.Content.Weak,
                          }}
                        >
                          {day.label}
                        </CustomTypography>
                        {selectedDaySet.has(day.key) && (
                          <CheckIconSvgSelectedButton
                            color={theme.vars.palette.Content.Highlight}
                          />
                        )}
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              </Box>

              {isEdit && (
                <Button
                  fullWidth
                  size="lg"
                  variant="Soft"
                  color="danger"
                  sx={{ mt: "12px" }}
                  disabled={isDeleting}
                  onClick={() => setDeleteModalOpen(true)}
                >
                  알림 제거
                </Button>
              )}
            </ContentTemplateV2>
          </Box>
        </Box>
      </Box>

      {/* Modal 유지 */}
      <CustomModal
        open={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        title={"저장되지 않았어요"}
        subtitle={
          isEdit
            ? "알림을 변경하지 않고 나갈까요?"
            : "알림을 추가하지 않고 나갈까요?"
        }
        actions={[
          {
            label: "저장 않고 나가기",
            onClick: () => {
              setLeaveModalOpen(false);
              navigate(-1);
            },
            variant: "Soft",
            color: "danger",
          },
        ]}
      />

      <CustomModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={"정말로 제거할까요?"}
        subtitle={"설정해 둔 알림이 제거돼요."}
        actions={[
          {
            label: "제거하기",
            onClick: handleConfirmDelete,
            variant: "Solid",
            color: "danger",
          },
        ]}
      />
    </>
  );
}
